import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Bookmark, CalendarDays, Volume2 } from 'lucide-react';
import SearchBar, { useDefineSearch } from '../SearchBar';
import { HERO_CARDS, HERO_EXAMPLES } from '../../data/vocabulary';

// Where each floating card sits around the centre column (wide screens only)
const CARD_POSITIONS = [
  'left-0 top-[13%]',
  'right-0 top-[9%]',
  'left-8 bottom-[13%]',
  'right-6 bottom-[16%]',
];

const INDICATORS = [
  { icon: CalendarDays, label: 'A new word every day', tint: 'bg-mint' },
  { icon: Volume2, label: 'Hear how words sound', tint: 'bg-lavender' },
  { icon: Bookmark, label: 'Save words to learn', tint: 'bg-butter' },
];

const VocabCard = ({ card, className = '' }) => (
  <Link
    to={`/define/${card.word}`}
    className={`group block rounded-3xl border border-ink/10 p-5 text-left shadow-[0_22px_45px_-28px_rgb(var(--shade)/0.55)] transition-transform duration-300 ease-out hover:scale-[1.04] ${card.color} ${className}`}
  >
    <span className="text-sm text-ink-soft">{card.partOfSpeech}</span>
    <span className="mt-0.5 block font-display text-2xl text-ink">{card.word}</span>
    <span className="mt-2 block text-[0.95rem] leading-snug text-ink">{card.gloss}</span>
  </Link>
);

const Backdrop = () => (
  <div aria-hidden="true" className="pointer-events-none absolute inset-0 overflow-hidden">
    <div className="absolute -left-48 -top-48 h-[36rem] w-[36rem] rounded-full bg-lavender opacity-90 blur-3xl dark:hidden" />
    <div className="absolute -right-40 top-0 h-[32rem] w-[32rem] rounded-full bg-peach opacity-80 blur-3xl dark:hidden" />
    <div className="absolute -bottom-56 left-1/4 h-[30rem] w-[44rem] rounded-full bg-mint opacity-80 blur-3xl dark:hidden" />
    <span className="absolute left-[19%] top-[44%] hidden font-serif text-3xl italic text-ink/[0.08] xl:block">n.</span>
    <span className="absolute right-[21%] top-[47%] hidden font-serif text-3xl italic text-ink/[0.08] xl:block">/ˈwɜːd/</span>
    <span className="absolute bottom-[6%] left-1/2 hidden -translate-x-1/2 font-serif text-2xl italic text-ink/[0.07] xl:block">adj.</span>
  </div>
);

// The full placeholder doesn't fit beside the buttons on a phone
const isNarrowScreen = () => window.matchMedia?.('(max-width: 639px)').matches ?? false;

const Hero = () => {
  const runSearch = useDefineSearch();
  const [narrow] = useState(isNarrowScreen);

  return (
    <section className="relative isolate overflow-hidden border-b border-rule">
      <Backdrop />

      <div className="relative mx-auto w-full max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="relative flex flex-col items-center pb-12 pt-14 text-center sm:pb-16 sm:pt-20 lg:min-h-[clamp(780px,calc(100svh-4rem),880px)] lg:justify-center lg:py-16">
          {/* Floating vocabulary cards, kept clear of the centre column */}
          {HERO_CARDS.map((card, index) => (
            <div
              key={card.word}
              className={`card-arrive absolute hidden w-56 xl:block ${CARD_POSITIONS[index]}`}
              style={{ '--delay': `${550 + index * 140}ms` }}
            >
              <VocabCard card={card} className={`${card.tilt} hover:rotate-0`} />
            </div>
          ))}

          <p className="rise text-[0.95rem] font-semibold text-ink-soft" style={{ '--delay': '0ms' }}>
            Your daily dose of word discovery
          </p>

          <h1 className="mt-5 font-display text-[clamp(3rem,7.4vw,6.25rem)] leading-[1.02] tracking-[-0.02em] text-ink">
            <span className="rise block" style={{ '--delay': '120ms' }}>Words open up</span>
            <span className="rise block" style={{ '--delay': '240ms' }}>
              <span className="relative isolate inline-block px-[0.12em]">
                <span
                  aria-hidden="true"
                  className="absolute inset-x-0 bottom-[0.06em] top-[0.2em] -z-10 -rotate-1 rounded-[0.3em] bg-gradient-to-r from-lavender via-peach to-butter dark:bg-none dark:bg-ink/[0.09]"
                />
                new worlds.
              </span>
            </span>
          </h1>

          <p className="rise mt-6 max-w-xl text-balance text-lg leading-relaxed text-ink-soft sm:text-xl" style={{ '--delay': '360ms' }}>
            Discover meanings, master pronunciation, explore new expressions, and make better words part of your
            everyday life.
          </p>

          <div className="rise mt-10 w-full max-w-2xl" style={{ '--delay': '480ms' }}>
            <SearchBar variant="hero" placeholder={narrow ? 'Search a word…' : undefined} />
          </div>

          <div className="rise mt-5 flex flex-wrap items-center justify-center gap-2" style={{ '--delay': '580ms' }}>
            <span className="mr-1 text-[0.95rem] text-ink-soft">Try</span>
            {HERO_EXAMPLES.map((example) => (
              <button
                key={example}
                type="button"
                onClick={() => runSearch(example)}
                className="rounded-full border border-ink/10 bg-surface/70 px-4 py-2 text-[0.95rem] text-ink backdrop-blur-sm transition-[background-color,border-color,transform] hover:border-ink/30 hover:bg-surface active:scale-95"
              >
                {example}
              </button>
            ))}
          </div>

          <ul className="rise mt-10 flex flex-wrap justify-center gap-x-8 gap-y-3" style={{ '--delay': '680ms' }}>
            {INDICATORS.map(({ icon: Icon, label, tint }) => (
              <li key={label} className="flex items-center gap-2.5 text-[0.95rem] text-ink">
                <span className={`grid h-8 w-8 place-items-center rounded-full ${tint}`}>
                  <Icon size={16} aria-hidden="true" />
                </span>
                {label}
              </li>
            ))}
          </ul>

          {/* Narrower screens: the same cards as a swipeable row below the search */}
          <div className="scroll-row -mx-4 mt-8 flex w-[calc(100%+2rem)] snap-x snap-mandatory scroll-px-4 gap-3 overflow-x-auto px-4 pb-10 pt-4 sm:-mx-6 sm:w-[calc(100%+3rem)] sm:scroll-px-6 sm:px-6 xl:hidden">
            {HERO_CARDS.map((card) => (
              <VocabCard key={card.word} card={card} className="w-60 shrink-0 snap-start" />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
